import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AdminNavbar from "../AdminNav";
import {
  Box,
  Typography,
  Button,
  ButtonBase,
  IconButton,
  CircularProgress,
  Paper,
  Tabs,
  Tab,
} from "@mui/material";
import DescriptionIcon from "@mui/icons-material/Description";
import ImageIcon from "@mui/icons-material/Image";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import TheatersIcon from "@mui/icons-material/Theaters";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import AttachFileOutlinedIcon from "@mui/icons-material/AttachFileOutlined";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import api from "../../api/api";
import { ENDPOINTS } from "../../api/endpoints";
import { useLocationLabels } from "../../hooks/useLocationLabels";
import { addSpaceAfterCommas } from "../../utils/textUtils";

const isImageFile = (path) => {
  if (!path) return false;
  const cleanPath = String(path).trim().split(/[?#]/)[0];
  return /\.(jpe?g|png|gif|bmp|webp|avif)$/i.test(cleanPath);
};

const Pipe = () => (
  <span style={{ color: "#aeb8c8", margin: "0 8px" }}>|</span>
);

const Label = ({ children }) => (
  <span style={{ color: "#17233f", fontWeight: 600 }}>{children}</span>
);

const FileLink = ({ url, label }) => {
  if (!url) return null;

  const isImage = /\.(jpe?g|png|gif|bmp|webp|avif)$/i.test(
    String(url).split(/[?#]/)[0]
  );
  const Icon = isImage ? ImageIcon : DescriptionIcon;

  return (
    <ButtonBase
      onClick={() => window.open(url, "_blank", "noopener")}
      sx={{
        display: "flex",
        justifyContent: "flex-start",
        alignItems: "center",
        gap: 1.5,
        width: "100%",
        minHeight: 58,
        p: 1.5,
        border: "1px solid #e5ebf4",
        borderRadius: "8px",
        textAlign: "left",
        color: "#17233f",
        backgroundColor: "#fff",
        "&:hover": {
          borderColor: "#90b5fe",
          backgroundColor: "#f8faff",
        },
      }}
    >
      <Box
        sx={{
          width: 38,
          height: 40,
          flexShrink: 0,
          display: "grid",
          placeItems: "center",
          borderRadius: "6px",
          bgcolor: "rgba(144,181,254,0.18)",
          color: "#246fdb",
        }}
      >
        <Icon />
      </Box>
      <Typography fontWeight={700} fontSize="0.9rem">
        {label}
      </Typography>
    </ButtonBase>
  );
};

const ViewNotice = () => {
  const { noticeId } = useParams();
  const navigate = useNavigate();

  const [project, setProject] = useState(null);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeNoticeTab, setActiveNoticeTab] = useState("overview");
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState(0);

  const { loading: locLoading, formatLocations } = useLocationLabels();

  useEffect(() => {
    const fetchNotice = async () => {
      try {
        const [projRes, rolesRes] = await Promise.all([
          api.get(
            ENDPOINTS.getNoticeDetails({
              noticeId,
              email: "",
              page: 1,
              pageSize: 50,
            })
          ),
          api.get(
            ENDPOINTS.getRoleDetails({
              noticeId,
              page: 1,
              pageSize: 50,
            })
          ),
        ]);

        const projects = projRes?.data?.data || [];
        setProject(
          projects.find(
            (p) => Number(p.noticeId) === Number(noticeId)
          ) || null
        );
        setRoles(rolesRes?.data?.data?.items || []);
      } catch (error) {
        console.error("Error fetching notice:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchNotice();
  }, [noticeId]);

  if (loading || locLoading) {
    return (
      <>
        <AdminNavbar />
        <Box
          sx={{
            height: "80vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress />
        </Box>
      </>
    );
  }

  if (!project) return null;

  const photoReferenceItems = [
    project.photoReferencesPath &&
      isImageFile(project.photoReferencesPath) && {
        url: project.photoReferencesPath,
        label: "Project Photo Reference",
        roleName: null,
      },
    project.picturesandDocumentsPath &&
      isImageFile(project.picturesandDocumentsPath) && {
        url: project.picturesandDocumentsPath,
        label: "Project Material",
        roleName: null,
      },
    ...roles
      .filter((role) => role.photoorScriptPath && isImageFile(role.photoorScriptPath))
      .map((role) => ({
        url: role.photoorScriptPath,
        label: `${role.roleName || "Role"} Reference`,
        roleName: role.roleName || "Role",
      })),
  ].filter(Boolean);

  const uniquePhotoReferences = [
    ...new Map(photoReferenceItems.map((item) => [item.url, item])).values(),
  ];

  // Only the Script Reference is displayed as a project document.
  const uniqueDocumentReferences = project.scriptsPath
    ? [{ url: project.scriptsPath, label: "Script Reference" }]
    : [];

  const hasProjectMaterials =
    uniquePhotoReferences.length > 0 || uniqueDocumentReferences.length > 0;

  const handleEdit = () => {
    sessionStorage.setItem("editNoticeId", project.noticeId);
    sessionStorage.setItem("editNoticeData", JSON.stringify(project));

    navigate(`/post-casting-call/${project.noticeId}`, {
      state: {
        casting: project,
        noticeId: project.noticeId,
      },
    });
  };

  return (
    <>
      <AdminNavbar />

      <Box
        sx={{
          maxWidth: 1440,
          mx: "auto",
          px: { xs: 1.5, md: 3 },
          py: { xs: 2, md: 3 },
          color: "#17233f",
        }}
      >
        {/* Casting header - Rush Call stays as the left vertical ribbon */}
        <Paper
          elevation={0}
          sx={{
            position: "relative",
            overflow: "hidden",
            display: "flex",
            background:
              "linear-gradient(115deg, #202020 0%, #182633 100%)",
            color: "#fff",
            borderRadius: "8px",
            mb: 2,
          }}
        >
          {project.rushCall && (
            <Box
              sx={{
                flex: "0 0 46px",
                bgcolor: "#d17c7c",
                color: "#fff",
                writingMode: "vertical-rl",
                transform: "rotate(180deg)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 800,
                fontSize: 13,
                letterSpacing: 0.5,
                py: 1.5,
              }}
            >
              RUSH CALL
            </Box>
          )}

          <Box
            sx={{
              display: "flex",
              flex: 1,
              minWidth: 0,
              p: { xs: 2, md: 2 },
            }}
          >
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                component="h1"
                sx={{
                  fontSize: { xs: "1.65rem", md: "2.25rem" },
                  lineHeight: 1.12,
                  fontWeight: 800,
                  letterSpacing: "-0.035em",
                  overflowWrap: "anywhere",
                  mb: 1.1,
                }}
              >
                {project.projectTitle}
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1,
                  mb: 2,
                }}
              >
                <LocationOnIcon
                  sx={{ color: "#fff", mt: "1px", flexShrink: 0 }}
                />
                <Typography
                  sx={{
                    fontSize: { xs: 14, md: 16 },
                    color: "#f3f6fb",
                    lineHeight: 1.45,
                  }}
                >
                  <Box component="span" sx={{ color: "#cbd5e1", mr: 0.75 }}>
                    Seeking Talent From:
                  </Box>
                  <Box component="span" sx={{ color: "#90b5fe" }}>
                    {formatLocations(project.auditionLocations)}
                  </Box>
                </Typography>
              </Box>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "repeat(2, minmax(0, 1fr))",
                    sm: "repeat(2, minmax(0, 1fr))",
                    lg: "minmax(0, 0.9fr) minmax(0, 0.85fr) minmax(0, 0.9fr) minmax(240px, 1.8fr)",
                  },
                  rowGap: 2,
                  columnGap: 0,
                  mt: 1.5,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    gap: 1,
                    alignItems: "center",
                    pr: 1.5,
                  }}
                >
                  <TheatersIcon
                    sx={{ fontSize: 30, color: "#fff", flexShrink: 0 }}
                  />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ color: "#cbd5e1", fontSize: 12 }}>
                      Union Status
                    </Typography>
                    <Typography
                      sx={{
                        color: "#fff",
                        fontSize: 14,
                        fontWeight: 600,
                        overflowWrap: "anywhere",
                      }}
                    >
                      {addSpaceAfterCommas(project.unionStatus || "-")}
                    </Typography>
                  </Box>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    gap: 1,
                    alignItems: "center",
                    px: { sm: 1.5 },
                    borderLeft: { sm: "1px solid rgba(203,213,225,0.28)" },
                  }}
                >
                  <CalendarMonthIcon
                    sx={{ fontSize: 29, color: "#fff", flexShrink: 0 }}
                  />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ color: "#cbd5e1", fontSize: 12 }}>
                      Posted
                    </Typography>
                    <Typography sx={{ color: "#fff", fontSize: 14, fontWeight: 600 }}>
                      {project.noticeCreatedDate
                        ? new Date(project.noticeCreatedDate).toLocaleDateString()
                        : "N/A"}
                    </Typography>
                  </Box>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    gap: 1,
                    alignItems: "center",
                    px: { sm: 1.5 },
                    borderLeft: { sm: "1px solid rgba(203,213,225,0.28)" },
                  }}
                >
                  <CalendarMonthIcon
                    sx={{ fontSize: 29, color: "#fff", flexShrink: 0 }}
                  />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ color: "#d17c7c", fontSize: 12 }}>
                      Deadline
                    </Typography>
                    <Typography
                      sx={{ color: "#d17c7c", fontSize: 16, fontWeight: 800 }}
                    >
                      {project.noticeEndDate
                        ? new Date(project.noticeEndDate).toLocaleDateString()
                        : "N/A"}
                    </Typography>
                  </Box>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    gap: 1,
                    alignItems: "center",
                    px: { sm: 1.5 },
                    borderLeft: { sm: "1px solid rgba(203,213,225,0.28)" },
                    minWidth: 0,
                  }}
                >
                  <AttachMoneyIcon
                    sx={{ fontSize: 32, color: "#fff", flexShrink: 0 }}
                  />
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography sx={{ color: "#cbd5e1", fontSize: 12 }}>
                      Pay
                    </Typography>
                    <Typography
                      sx={{
                        color: "#fff",
                        fontSize: 14,
                        fontWeight: 600,
                        overflowWrap: "anywhere",
                        lineHeight: 1.5,
                      }}
                    >
                      {project.payScale || "N/A"}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        </Paper>

        {/* Notice tabs */}
        <Box sx={{ mt: 2 }}>
          <Box
            sx={{
              borderBottom: "1px solid #dfe7f2",
              bgcolor: "transparent",
              mb: 2,
            }}
          >
            <Tabs
              value={activeNoticeTab}
              onChange={(_, nextTab) => setActiveNoticeTab(nextTab)}
              variant="scrollable"
              scrollButtons="auto"
              aria-label="Casting notice sections"
              sx={{
                minHeight: 48,
                "& .MuiTab-root": {
                  minHeight: 48,
                  textTransform: "none",
                  fontWeight: 700,
                  color: "#68758f",
                  fontSize: "0.95rem",
                  px: { xs: 1.5, md: 2.5 },
                },
                "& .Mui-selected": { color: "#246fdb" },
                "& .MuiTabs-indicator": {
                  height: 3,
                  borderRadius: "3px 3px 0 0",
                  backgroundColor: "#246fdb",
                },
              }}
            >
              <Tab value="overview" label="Overview" />
              <Tab value="roles" label={`Roles (${roles.length})`} />
              {hasProjectMaterials && (
                <Tab value="materials" label="Project Materials" />
              )}
            </Tabs>
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "minmax(0, 1fr)",
                lg:
                  activeNoticeTab === "materials" || !hasProjectMaterials
                    ? "minmax(0, 1fr)"
                    : "minmax(0, 1.7fr) minmax(320px, 1fr)",
              },
              gap: { xs: 2, md: 2.5 },
              alignItems: "start",
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              {/* Overview */}
              {activeNoticeTab === "overview" && (
                <>
                  <Paper
                    elevation={0}
                    sx={{
                      bgcolor: "#fff",
                      p: { xs: 1.5, md: 2.25 },
                      border: "1px solid #e5ebf4",
                      borderRadius: "10px",
                      boxShadow: "0 2px 10px rgba(24, 45, 75, 0.04)",
                      color: "#17233f",
                    }}
                  >
                    <Typography
                      component="h2"
                      sx={{
                        fontSize: { xs: "1.15rem", md: "1.35rem" },
                        fontWeight: 750,
                        letterSpacing: "-0.4px",
                        color: "#101d3a",
                        mb: 0.75,
                      }}
                    >
                      About the Project
                    </Typography>

                    {project.projectDescription
                      ?.split(/\r\n\r\n/)
                      .filter(Boolean)
                      .map((paragraph, index) => (
                        <Typography
                          key={index}
                          component="div"
                          sx={{
                            fontSize: "1rem",
                            lineHeight: 1.5,
                            color: "#000",
                            mb: 0.5,
                            overflowWrap: "anywhere",
                            "& p": { mb: 0.5 },
                          }}
                          dangerouslySetInnerHTML={{ __html: paragraph }}
                        />
                      ))}
                  </Paper>

                  {/* Roles remain below Overview, as in the reference layout */}
                  {roles.length > 0 && (
                    <Box sx={{ mt: 1.5, minWidth: 0 }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          px: { xs: 0.5, md: 1.5 },
                          pt: 0.5,
                          pb: 1,
                        }}
                      >
                        <GroupsOutlinedIcon
                          sx={{ color: "#101d3a", fontSize: 27 }}
                        />
                        <Typography
                          component="h2"
                          sx={{
                            fontSize: { xs: "1.1rem", md: "1.25rem" },
                            fontWeight: 750,
                            color: "#101d3a",
                            letterSpacing: "-0.3px",
                          }}
                        >
                          Available Roles
                        </Typography>
                      </Box>

                      {roles.map((role) => (
                        <Box
                          key={role.roleId}
                          sx={{
                            minWidth: 0,
                            mx: { xs: 0, md: 1.5 },
                            mb: 1,
                            p: { xs: 1.25, md: 1.5 },
                            border: "1px solid #e2eaf5",
                            borderLeft: "4px solid #000",
                            borderRadius: "8px",
                            bgcolor: "#fff",
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: "1.05rem",
                              fontWeight: 700,
                              color: "#90b5fe",
                              mb: 0.75,
                            }}
                          >
                            {role.roleName}
                          </Typography>

                          {role.sex && (
                            <Typography
                              sx={{
                                fontSize: "0.9rem",
                                lineHeight: 1.5,
                                color: "#000",
                              }}
                            >
                              <Label>Sex:</Label>
                              <Pipe />
                              {addSpaceAfterCommas(role.sex)}
                            </Typography>
                          )}

                          {role.moreAboutRole && (
                            <Typography
                              component="div"
                              sx={{
                                mt: 0.35,
                                fontSize: "1rem",
                                lineHeight: 1.5,
                                color: "#000",
                                overflowWrap: "anywhere",
                              }}
                              dangerouslySetInnerHTML={{
                                __html: role.moreAboutRole.replace(/\r\n/g, "<br/>"),
                              }}
                            />
                          )}
                        </Box>
                      ))}
                    </Box>
                  )}
                </>
              )}

              {/* Roles tab */}
              {activeNoticeTab === "roles" && (
                <Box sx={{ minWidth: 0 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      px: { xs: 0.5, md: 1.5 },
                      pt: 0.5,
                      pb: 1,
                    }}
                  >
                    <GroupsOutlinedIcon
                      sx={{ color: "#101d3a", fontSize: 27 }}
                    />
                    <Typography
                      component="h2"
                      sx={{
                        fontSize: { xs: "1.1rem", md: "1.25rem" },
                        fontWeight: 750,
                        color: "#101d3a",
                      }}
                    >
                      Available Roles
                    </Typography>
                  </Box>

                  {roles.length === 0 ? (
                    <Paper
                      elevation={0}
                      sx={{
                        p: 4,
                        textAlign: "center",
                        border: "1px solid #e5ebf4",
                        borderRadius: "10px",
                      }}
                    >
                      <Typography color="text.secondary">
                        No roles available.
                      </Typography>
                    </Paper>
                  ) : (
                    roles.map((role) => (
                      <Box
                        key={role.roleId}
                        sx={{
                          minWidth: 0,
                          mx: { xs: 0, md: 1.5 },
                          mb: 1,
                          p: { xs: 1.25, md: 1.5 },
                          border: "1px solid #e2eaf5",
                          borderLeft: "4px solid #000",
                          borderRadius: "8px",
                          bgcolor: "#fff",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "1.05rem",
                            fontWeight: 700,
                            color: "#90b5fe",
                            mb: 0.75,
                          }}
                        >
                          {role.roleName}
                        </Typography>

                        {role.sex && (
                          <Typography
                            sx={{
                              fontSize: "0.9rem",
                              lineHeight: 1.5,
                              color: "#000",
                            }}
                          >
                            <Label>Sex:</Label>
                            <Pipe />
                            {addSpaceAfterCommas(role.sex)}
                          </Typography>
                        )}

                        {role.moreAboutRole && (
                          <Typography
                            component="div"
                            sx={{
                              mt: 0.35,
                              fontSize: "1rem",
                              lineHeight: 1.5,
                              color: "#000",
                              overflowWrap: "anywhere",
                            }}
                            dangerouslySetInnerHTML={{
                              __html: role.moreAboutRole.replace(/\r\n/g, "<br/>"),
                            }}
                          />
                        )}
                      </Box>
                    ))
                  )}
                </Box>
              )}

              {/* Materials tab uses the same content, while the sidebar remains visible */}
              {activeNoticeTab === "materials" && (
                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 1.5, md: 2.25 },
                    border: "1px solid #e5ebf4",
                    borderRadius: "10px",
                    boxShadow: "0 2px 10px rgba(24,45,75,0.04)",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "1.15rem",
                      fontWeight: 750,
                      color: "#101d3a",
                      mb: 1,
                    }}
                  >
                    Project Materials
                  </Typography>
                  <Typography sx={{ color: "#68758f", fontSize: "0.9rem" }}>
                    View the photo references and project document in the panel.
                  </Typography>
                </Paper>
              )}
            </Box>

            {/* Project Materials sidebar - visible beside Overview and Roles */}
            {hasProjectMaterials && (
              <Box
                sx={{
                  minWidth: 0,
                  bgcolor: "#fff",
                  p: { xs: 1.5, md: 2 },
                  border: "1px solid #e5ebf4",
                  borderRadius: "10px",
                  boxShadow: "0 2px 10px rgba(24,45,75,0.04)",
                  position: { lg: "sticky" },
                  top: { lg: 16 },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mb: 1.5,
                  }}
                >
                  <AttachFileOutlinedIcon
                    sx={{ color: "#90b5fe", fontSize: 26 }}
                  />
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 750, color: "#17233f" }}
                  >
                    Project Materials
                  </Typography>
                </Box>

                {/* Photo reference carousel */}
                {uniquePhotoReferences.length > 0 && (() => {
                  const activePhotoIndex =
                    selectedPhotoIndex % uniquePhotoReferences.length;
                  const activePhoto = uniquePhotoReferences[activePhotoIndex];

                  const showPreviousPhoto = () =>
                    setSelectedPhotoIndex((current) =>
                      (current - 1 + uniquePhotoReferences.length) %
                      uniquePhotoReferences.length
                    );

                  const showNextPhoto = () =>
                    setSelectedPhotoIndex((current) =>
                      (current + 1) % uniquePhotoReferences.length
                    );

                  return (
                    <Box sx={{ mb: 2 }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.75,
                          px: 1,
                          py: 0.5,
                          border: "1px solid #e5ebf4",
                          borderRadius: "8px 8px 0 0",
                          minHeight: 40,
                        }}
                      >
                        <ImageIcon sx={{ color: "#90b5fe", fontSize: 23 }} />
                        <Typography
                          sx={{
                            fontWeight: 700,
                            color: "#17233f",
                            fontSize: 13,
                          }}
                        >
                          Photo Reference
                        </Typography>
                        <Typography sx={{ color: "#68758f", fontSize: 12 }}>
                          ({uniquePhotoReferences.length} images)
                        </Typography>
                        <Box sx={{ flex: 1 }} />
                        <IconButton
                          size="small"
                          aria-label="Previous photo"
                          onClick={showPreviousPhoto}
                          disabled={uniquePhotoReferences.length < 2}
                          sx={{ p: 0.25, color: "#68758f" }}
                        >
                          <ChevronLeftIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          aria-label="Next photo"
                          onClick={showNextPhoto}
                          disabled={uniquePhotoReferences.length < 2}
                          sx={{ p: 0.25, color: "#68758f" }}
                        >
                          <ChevronRightIcon fontSize="small" />
                        </IconButton>
                      </Box>

                      <Box
                        sx={{
                          border: "1px solid #e5ebf4",
                          borderTop: 0,
                          borderRadius: "0 0 8px 8px",
                          overflow: "hidden",
                          bgcolor: "#f5f7fb",
                        }}
                      >
                        {activePhoto.roleName && (
                          <Box
                            sx={{
                              px: 1.25,
                              py: 0.75,
                              bgcolor: "#fff",
                              borderBottom: "1px solid #e5ebf4",
                            }}
                          >
                            <Typography sx={{ color: "#68758f", fontSize: 12 }}>
                              Role:{" "}
                              <Box
                                component="span"
                                sx={{ color: "#17233f", fontWeight: 700 }}
                              >
                                {activePhoto.roleName}
                              </Box>
                            </Typography>
                          </Box>
                        )}

                        <Box
                          component="a"
                          href={activePhoto.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            display: "block",
                            lineHeight: 0,
                            bgcolor: "#111",
                          }}
                        >
                          <Box
                            component="img"
                            key={activePhoto.url}
                            src={activePhoto.url}
                            alt={activePhoto.label}
                            loading="lazy"
                            onError={(event) => {
                              console.error(
                                "Project material image failed to load:",
                                event.currentTarget.src
                              );
                              event.currentTarget.style.visibility = "hidden";
                            }}
                            sx={{
                              display: "block",
                              width: "100%",
                              height: 250,
                              objectFit: "contain",
                              bgcolor: "#111",
                            }}
                          />
                        </Box>

                        {uniquePhotoReferences.length > 1 && (
                          <Box
                            sx={{
                              display: "flex",
                              gap: 0.75,
                              p: 0.75,
                              overflowX: "auto",
                              scrollBehavior: "smooth",
                              "&::-webkit-scrollbar": { height: 5 },
                              "&::-webkit-scrollbar-thumb": {
                                backgroundColor: "#cbd5e1",
                                borderRadius: 10,
                              },
                            }}
                          >
                            {uniquePhotoReferences.map((photo, index) => (
                              <ButtonBase
                                key={`${photo.url}-${index}`}
                                onClick={() => setSelectedPhotoIndex(index)}
                                aria-label={`Show photo ${index + 1}`}
                                sx={{
                                  flex: "0 0 74px",
                                  width: 74,
                                  height: 50,
                                  overflow: "hidden",
                                  borderRadius: "5px",
                                  border:
                                    index === activePhotoIndex
                                      ? "2px solid #1976d2"
                                      : "1px solid #d8e0ec",
                                  opacity: index === activePhotoIndex ? 1 : 0.82,
                                }}
                              >
                                <Box
                                  component="img"
                                  src={photo.url}
                                  alt={photo.label}
                                  loading="lazy"
                                  sx={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "cover",
                                    display: "block",
                                  }}
                                />
                              </ButtonBase>
                            ))}
                          </Box>
                        )}
                      </Box>
                    </Box>
                  );
                })()}

                {/* Only the Script Reference is shown as a document */}
                {uniqueDocumentReferences.map((file, index) => (
                  <Box
                    key={`${file.url}-${index}`}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.25,
                      p: 1.25,
                      mb: 1,
                      border: "1px solid #e5ebf4",
                      borderRadius: "8px",
                      "&:hover": {
                        borderColor: "#90b5fe",
                        bgcolor: "#f8faff",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 42,
                        height: 46,
                        flexShrink: 0,
                        display: "grid",
                        placeItems: "center",
                        borderRadius: "6px",
                        bgcolor: "rgba(144,181,254,0.18)",
                        color: "#90b5fe",
                      }}
                    >
                      <DescriptionIcon />
                    </Box>

                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography sx={{ fontWeight: 700, color: "#17233f" }}>
                        {file.label}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: "#68758f", fontSize: "0.8rem" }}
                      >
                        Project document
                      </Typography>
                    </Box>

                    <IconButton
                      component="a"
                      href={file.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`Open ${file.label}`}
                      sx={{ color: "#246fdb" }}
                    >
                      <FileDownloadOutlinedIcon />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </Box>

        {/* Existing actions */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 1.5,
            mt: 3,
          }}
        >
          <Button
            variant="contained"
            onClick={handleEdit}
            sx={{
              backgroundColor: "#246fdb",
              textTransform: "none",
              fontWeight: 700,
              px: 3,
              borderRadius: "7px",
              "&:hover": { backgroundColor: "#185dbd" },
            }}
          >
            Edit Notice
          </Button>

          <Button
            variant="outlined"
            onClick={() => window.close()}
            sx={{
              color: "#46536a",
              borderColor: "#d5deeb",
              textTransform: "none",
              fontWeight: 700,
              px: 3,
              borderRadius: "7px",
            }}
          >
            Close
          </Button>
        </Box>
      </Box>
    </>
  );
};

export default ViewNotice;
